/* ============================================================
 * ADG Scroll Memory — come back to where you were.
 *
 * Two journeys, and they fail for different reasons, so they are solved
 * differently rather than by one mechanism pretending to cover both:
 *
 *   tiles -> design grid -> back
 *     Our back button is a NEW navigation as far as the browser is
 *     concerned, so the browser has no scroll to restore. The bar prefers
 *     history.back() when we really did come from there — then the browser
 *     restores natively and none of this runs. This is the fallback for
 *     arriving by a shared link, where there is no history entry.
 *
 *   design grid -> product -> Back
 *     A real history Back, so the browser DOES try. It fails anyway,
 *     because the grid is drawn by JS from the artwork index and only the
 *     first batch exists when the browser restores: the page is a third of
 *     its old height and the scroll clamps. Measured: 74 cards at 5512px
 *     came back as 24 cards at 427px. Fixing that means redrawing the
 *     batch count first, which is adg-artgrid's job — this module only
 *     holds the number and the scroll helper.
 *
 * WHY sessionStorage. It survives a real navigation to another URL, it is
 * per-tab so two tabs cannot fight, and it dies with the tab.
 *
 * THE GUARDS ARE THE POINT. The failure to design against is not "it
 * forgot" — it is someone arriving from a search result and being dumped
 * halfway down a page with no idea why. Nothing restores unless the saved
 * entry is for THIS exact path, it is recent, and either our own control
 * promised a return or the browser says this navigation is a Back.
 * ============================================================ */
(function () {
  'use strict';

  /* Loaded by every section that needs it (tiles, designs, BYO), and two of
     those share collection.json — so this file WILL be included twice on one
     page. Second copy bows out: one API object, one click listener, one boot.
     Without this the delegated listener registered twice per extra copy. */
  if (window.ADGScrollMemory) return;

  var WHERE = 'adg:scroll-from';   // { p, y, t, n?, seq }
  var BACK  = 'adg:scroll-return'; // set only by a control that promises a return
  var STALE = 10 * 60 * 1000;      // forget a position after ten minutes
  var SEQ   = 'adg:nav-seq';       // how many module-bearing pages this tab has visited

  /* THE PAIR RULE (Justin, 2026-08-05): a restore belongs to the immediate
     round trip — grid to product and straight back, tiles to grid and
     straight back. Come back by way of a third page and you should land at
     the top; being dropped mid-page an hour later "in a weird place" is the
     worse experience. Implementation: every page that carries this module
     bumps a per-tab counter; a saved position is honoured only within TWO
     bumps of when it was saved. Product pages carry no module, so the
     grid-product-Back journey costs one bump; the tiles-grid-back journey
     costs two. Anything longer is a wander, and a wander resets. */
  var seqNow = 0;
  try {
    seqNow = (parseInt(sessionStorage.getItem(SEQ), 10) || 0) + 1;
    sessionStorage.setItem(SEQ, String(seqNow));
  } catch (e) { /* private mode */ }

  /* THE TRAIL (08-08): the shopper's actual path, kept as a stack now that
     this module runs on EVERY page. A real Back pops it; anything else
     pushes. This is what lets the bar's Back always mean one step UP the
     path — the referrer-based guess could make a named back PUSH a new
     history entry, after which plain history-back ping-ponged between two
     pages forever (Justin hit the loop: art-prints ↔ monuments). */
  var TRAIL = 'adg:trail';
  var trail = [];
  try { trail = JSON.parse(sessionStorage.getItem(TRAIL) || '[]'); } catch (e) { /* private mode */ }
  (function tend() {
    var p = location.pathname;
    if (isHistoryNav() && trail.length >= 2 && trail[trail.length - 2] === p) {
      trail.pop();                       // stepped back up the path
    } else if (trail[trail.length - 1] !== p) {
      trail.push(p);                     // moved forward (a reload is a no-op)
      if (trail.length > 25) trail.shift();
    }
    try { sessionStorage.setItem(TRAIL, JSON.stringify(trail)); } catch (e) { /* private mode */ }
  })();

  function nowMs() { return new Date().getTime(); }
  function here() { return location.pathname + location.search; }

  function read(key) {
    try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (e) { return null; }
  }
  function write(key, val) {
    try { sessionStorage.setItem(key, JSON.stringify(val)); } catch (e) { /* private mode */ }
  }
  function drop(key) {
    try { sessionStorage.removeItem(key); } catch (e) { /* private mode */ }
  }

  /* Did the browser just go Back or Forward? This is the signal that makes
     the product -> grid journey work without us inventing our own flag for
     it. Navigation Timing L2 in every current browser; the deprecated L1
     field covers older ones; where neither exists we simply do not restore,
     which lands at the top — the safe direction to fail in. */
  function isHistoryNav() {
    try {
      var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (nav && nav.type) return nav.type === 'back_forward';
      if (performance.navigation) return performance.navigation.type === 2;
    } catch (e) { /* fall through */ }
    return false;
  }

  /* What this page calls itself. Taken from its own H1 rather than guessed
     from the URL, so the way back is labelled with the real name — "Shop by
     Collection", not a title-cased slug, and not a bare "Back". */
  function pageLabel() {
    var h = document.querySelector('h1');
    var t = h && h.textContent ? h.textContent.replace(/\s+/g, ' ').trim() : '';
    return (t && t.length <= 40) ? t : '';
  }

  var API = {
    /* Where the shopper came from, WITHOUT consuming the scroll position —
       the label is needed on arrival, the position only on the way back. */
    origin: function () {
      var saved = read(WHERE);
      if (!saved || !saved.p || saved.p === here()) return null;
      if (!saved.t || (nowMs() - saved.t) > STALE) return null;
      // SAME GUARDS AS recall(). This reader fills back buttons, that one
      // restores scroll, and for two days they disagreed about what a
      // journey was: recall() enforced the destination and the pair rule,
      // origin() enforced neither — so bars confidently offered ways back
      // from journeys that had expired, on pages the entry never pointed
      // at. That mismatch WAS the BYO hijack and the wrong-label bugs.
      // A button this cannot justify simply stays hidden.
      if (saved.to && location.pathname !== saved.to) return null;
      if (saved.seq && seqNow - saved.seq > 2) return null;
      return { path: saved.p, label: saved.label || '' };
    },

    /* One step UP the shopper's real path — '' when this is where they
       came in. The bar trusts this over document.referrer (which lies
       after bfcache restores and redirects). */
    previous: function () {
      return trail.length >= 2 ? trail[trail.length - 2] : '';
    },

    /* Save where we are. `extra` carries anything the surface needs to
       rebuild itself — the grid passes how many cards it had drawn. */
    remember: function (extra) {
      var entry = { p: here(), y: Math.round(window.pageYOffset || 0), t: nowMs(), seq: seqNow };
      if (extra) for (var k in extra) if (extra.hasOwnProperty(k)) entry[k] = extra[k];
      write(WHERE, entry);
    },

    /* Our own control is about to navigate somewhere the browser will treat
       as new. Nothing else ever sets this. */
    expectReturn: function () { write(BACK, 1); },

    /* One-shot. Returns the saved entry if every guard passes, else null,
       and clears either way so it can never fire twice. */
    recall: function () {
      var saved = read(WHERE);
      var promised = !!read(BACK);
      drop(BACK);
      if (!saved) return null;
      if (saved.p !== here()) return null;          // a different page entirely
      drop(WHERE);
      if (!promised && !isHistoryNav()) return null; // arrived some other way
      if (!saved.t || (nowMs() - saved.t) > STALE) return null;
      // The pair rule: only within the immediate round trip. A journey that
      // touched more pages than the pair allows is a fresh arrival.
      if (saved.seq && seqNow - saved.seq > 2) return null;
      if (!saved.y) return null;
      return saved;
    },

    /* Scroll there, and keep at it briefly while the document is still
       growing — tiles fill from an index fetch and images settle, so the
       page is shorter at DOMContentLoaded than it will be a moment later.
       Never scrolls past the end, so a too-early attempt cannot slam the
       page to the bottom and look wrong once it grows. */
    restore: function (y) {
      if (!y) return;
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      var tries = 0;
      (function land() {
        var max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        window.scrollTo(0, Math.min(y, max));
        if (++tries < 12 && Math.abs((window.pageYOffset || 0) - y) > 2) {
          setTimeout(land, 60);
        } else if ('scrollRestoration' in history) {
          history.scrollRestoration = 'auto';
        }
      })();
    }
  };
  window.ADGScrollMemory = API;

  /* ---- the tiles half, which needs no host ------------------------------
     Capture phase, because the tile is an <a> whose click a host may cancel
     to swap in place (BYO). Recording a position we never leave is harmless;
     nothing reads it without passing the guards. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    var a = t && t.closest ? t.closest('a[href]') : null;
    if (!a) return;

    // Leaving a tiles page by a tile: remember where we were.
    // UNLESS the tile lives in the builder's slot — there the click swaps
    // the panel in place and never navigates, so recording a "position to
    // return to" plants a phantom entry that dumps the shopper at the
    // bottom of the builder on a later, unrelated visit. That was Justin's
    // 08-05 bad experience, found in the wild. The builder keeps its own
    // in-page memory (tilesScrollY).
    if (a.classList.contains('adg-cat')) {
      if (!a.closest('[data-byo-tiles-slot]')) API.remember({ label: pageLabel(), to: a.pathname });
      return;
    }

    // GOING BACK BY ANY ROUTE AT ALL. Not just the bottom bar — the
    // breadcrumb at the top of the page is the same journey and was landing
    // people at the top of a page they had already scrolled, because it is a
    // plain link that never told anyone it was a return. Two controls saying
    // "Canvas Prints" on one screen, behaving differently.
    //
    // So the rule lives here instead of in each control: any same-origin link
    // pointing at the page we remembered IS a return, whoever drew it and
    // whenever it was added. Nothing new has to opt in.
    //
    // WITH ONE CONDITION, added 2026-08-05 after the mega menu proved the
    // rule too broad: the return must be clicked FROM the page the journey
    // went to. Leaving a collection for a product and clicking that
    // product's breadcrumb is a return; opening the same collection from
    // the mega menu three pages later is an arrival, and an arrival starts
    // at the top. The saved entry carries `to` — where the shopper was
    // headed when we remembered — and only that page may arm a return.
    var saved = read(WHERE);
    if (!saved || !saved.p) return;
    var href = a.getAttribute('href') || '';
    if (href.charAt(0) === '#' || /^[a-z]+:/i.test(href) && href.indexOf(location.origin) !== 0) return;
    if (saved.to && location.pathname !== saved.to) return;
    if (a.pathname === saved.p && a.host === location.host) API.expectReturn();
  }, true);

  /* A tile-click entry carries no `n`. One that does belongs to the design
     grid, and adg-artgrid restores that itself after it has redrawn — this
     must not consume it first. */
  var saved = read(WHERE);
  if (saved && saved.n) return;

  function boot() {
    var s = API.recall();
    if (s) API.restore(s.y);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
